import { memo, useMemo } from 'react';
import {
    Box, Flex, Text, Heading, Divider, Skeleton,
} from '@chakra-ui/react';
import { addCommasToNumber, calculateCartTotal } from 'utils/utils';
import CartBox from 'components/Cart/CartBox';
import { useCurrency, useNairaToUSDToCurrency } from 'hooks/useCurrency';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS — match Cart.js
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const FONT      = "'Bricolage Grotesque', sans-serif";
const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

/* ─────────────────────────────────────────────────────────────
   SUMMARY ROW — used for Subtotal, Shipping, Discount, Tax, Total
   ───────────────────────────────────────────────────────────── */
const SummaryRow = memo(function SummaryRow({
    label, value, symbol, isLoading, isTotal, helper,
}) {
    /* Display "—" when value is zero AND not the total; helps the
       Discount/Tax rows feel intentional instead of "always-zero noise" */
    const displayValue = (() => {
        if (isLoading) return null; /* skeleton renders instead */
        if (value == null) return '—';
        if (!isTotal && Number(value) === 0) return '—';
        return `${symbol || '₦'} ${addCommasToNumber(Number(value))}`;
    })();

    return (
        <Flex justify="space-between" align="flex-start" gap="10px">
            <Flex direction="column" gap="2px" minW="0">
                <Text
                    fontSize={isTotal ? '14px' : '12.5px'}
                    color={isTotal ? INK : MUTED}
                    fontWeight={isTotal ? '800' : '500'}
                    fontFamily={FONT}
                >
                    {label}
                </Text>
                {helper && (
                    <Text fontSize="10.5px" color={FAINT}>
                        {helper}
                    </Text>
                )}
            </Flex>

            {isLoading ? (
                <Skeleton
                    h={isTotal ? '16px' : '13px'}
                    w="80px"
                    borderRadius="4px"
                    startColor={SURFACE_ALT}
                    endColor={BORDER}
                />
            ) : (
                <Text
                    fontSize={isTotal ? '16px' : '13px'}
                    color={INK}
                    fontWeight={isTotal ? '800' : '600'}
                    fontFamily={FONT}
                    whiteSpace="nowrap"
                >
                    {displayValue}
                </Text>
            )}
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   MAIN — CHECKOUT ORDERS
   ───────────────────────────────────────────────────────────── */
export default function CheckoutOrders({
    cartItems, totalAmount, quoteId, quote, loading,
}) {
    /* All hooks called at top level — Rules of Hooks compliant */
    const subtotalCurrency = useCurrency(calculateCartTotal(cartItems));
    const shippingCurrency = useNairaToUSDToCurrency(quote);

    /* Derive display values from hook results */
    const subtotal = subtotalCurrency?.price ?? 0;
    const shipping = shippingCurrency?.price ?? 0;
    const discount = 0; /* Backend will populate later */
    const tax      = 0; /* Backend will populate later */
    const symbol   = subtotalCurrency?.symbol || '₦';

    /* Total = subtotal + shipping − discount + tax (with null guards) */
    const total = useMemo(() => {
        return Number(subtotal) + Number(shipping) - Number(discount) + Number(tax);
    }, [subtotal, shipping, discount, tax]);

    const items = cartItems?.items ?? [];
    const itemCount = items.length;

    return (
        <Box
            bg={SURFACE_ALT}
            p={{ base: '16px', md: '20px', lg: '28px' }}
            borderRadius="14px"
            fontFamily={FONT}
            border="1px solid"
            borderColor={BORDER}
        >
            {/* Header */}
            <Flex align="baseline" justify="space-between" mb="14px" gap="8px">
                <Heading
                    as="h3"
                    fontSize={{ base: '14px', md: '15px' }}
                    fontWeight="800"
                    color={INK}
                    fontFamily={FONT}
                    letterSpacing="-0.01em"
                >
                    Your order
                </Heading>
                <Text fontSize="11px" color={FAINT} fontWeight="700" letterSpacing="0.04em">
                    {itemCount} {itemCount === 1 ? 'ITEM' : 'ITEMS'}
                </Text>
            </Flex>

            {/* Items list — empty state OR list */}
            {itemCount === 0 ? (
                <Box
                    bg={SURFACE}
                    border="1px dashed"
                    borderColor={BORDER}
                    borderRadius="10px"
                    p="20px"
                    textAlign="center"
                >
                    <Text fontSize="12px" color={MUTED}>
                        No items in this order yet.
                    </Text>
                </Box>
            ) : (
                <Box
                    bg={SURFACE}
                    border="1px solid"
                    borderColor={BORDER}
                    borderRadius="10px"
                    p={{ base: '12px', md: '14px' }}
                    boxShadow={SHADOW_SM}
                >
                    <Flex direction="column">
                        {items.map((item, idx) => (
                            <Box key={item.item_id}>
                                <CartBox cartItem={item} quoteId={quoteId} readOnly />
                                {idx < items.length - 1 && (
                                    <Divider my="12px" borderColor={BORDER} />
                                )}
                            </Box>
                        ))}
                    </Flex>
                </Box>
            )}

            {/* Cost breakdown */}
            <Box mt="20px">
                <Flex direction="column" gap="10px">
                    <SummaryRow
                        label="Subtotal"
                        value={subtotal}
                        symbol={symbol}
                    />
                    <SummaryRow
                        label="Shipping cost"
                        value={shipping}
                        symbol={symbol}
                        isLoading={loading}
                        helper={!loading && shipping === 0 ? 'Calculated at dispatch step' : null}
                    />
                    <SummaryRow
                        label="Discount"
                        value={discount}
                        symbol={symbol}
                        helper={discount > 0 ? null : null}
                    />
                    <SummaryRow
                        label="Tax"
                        value={tax}
                        symbol={symbol}
                    />
                </Flex>

                <Divider my="16px" borderColor={BORDER} />

                <SummaryRow
                    label="Total"
                    value={total}
                    symbol={symbol}
                    isLoading={loading}
                    isTotal
                />
            </Box>
        </Box>
    );
}